import { Button, Grid, Container, Typography, Box, styled, Paper, Chip, useTheme, alpha } from '@mui/material';
import DownloadIcon from '@mui/icons-material/DownloadForOffline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { motion } from 'framer-motion';

const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 50%, ${alpha(theme.palette.primary.main, 0.03)} 0%, transparent 50%)`,
    pointerEvents: 'none',
  }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: theme.spacing(4),
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.98)} 100%)`,
  backdropFilter: 'blur(10px)',
  boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.1)}`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '50px',
  padding: '12px 48px',
  fontSize: '1.1rem',
  fontWeight: 'bold',
  textTransform: 'none',
  position: 'relative',
  overflow: 'hidden',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const FeatureItem = ({ icon, text }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Box>
  );
};

const PDFDownloadPage = () => {
  const theme = useTheme();
  const pdfLocation = '/assets/images/MENSUELLE__2023.pdf';

  const handleDownload = async () => {
    try {
      const response = await fetch(pdfLocation);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'MENSUELLE_2023.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  return (
    <StyledContainer maxWidth="md">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{ width: '100%' }}
      >
        <StyledPaper elevation={0}>
          {/* Icon Badge */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                margin: '0 auto 24px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PictureAsPdfIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            </Box>
          </motion.div>

          {/* Title Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                background: `linear-gradient(135deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                mb: 2
              }}
            >
              Déclaration Mensuelle
            </Typography>
            
            <Typography 
              variant="subtitle1" 
              color="textSecondary"
              sx={{ mb: 4, maxWidth: '80%', margin: '0 auto' }}
            >
              Téléchargez le rapport financier mensuel complet et obtenez une vue détaillée de vos performances
            </Typography>
          </motion.div>

          {/* Features Grid */}
          <Grid container spacing={2} sx={{ mb: 5, mt: 2 }}>
            <Grid item xs={12} sm={4}>
              <FeatureItem 
                icon={<CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />}
                text="Données certifiées"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FeatureItem 
                icon={<CloudDownloadIcon sx={{ color: 'info.main', fontSize: 20 }} />}
                text="Téléchargement instantané"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FeatureItem 
                icon={<PictureAsPdfIcon sx={{ color: 'error.main', fontSize: 20 }} />}
                text="Format PDF sécurisé"
              />
            </Grid>
          </Grid>

          {/* Download Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <StyledButton
              variant="contained"
              size="large"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Télécharger le rapport
            </StyledButton>
          </motion.div>

          {/* File Info */}
          <Box mt={4}>
            <Chip
              icon={<PictureAsPdfIcon />}
              label="PDF • 1.1 MB"
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                fontWeight: 'medium',
                '& .MuiChip-icon': {
                  color: 'primary.main',
                }
              }}
            />
          </Box>

          {/* Additional Info */}
          <Box mt={3}>
            <Typography variant="caption" color="textSecondary" sx={{ opacity: 0.7 }}>
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
            </Typography>
          </Box>
        </StyledPaper>
      </motion.div>
    </StyledContainer>
  );
};

export default PDFDownloadPage;